import { forwardRef, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { utc_to_local_12_hour_clock } from "../../../utils/date_time_utils";
import GoogleMapComponent from "../GoogleMapComponent";
import MapPopupContent from "../MapPopupContent";
import Grid from "@mui/material/Grid2";
import moment from "moment";

const Map = forwardRef(function AssetHistoryMap(props, ref) {
  const {
    activeMarker = null,
    setActiveMarker = () => { },
    eventTypesMap,
    facilities,
    mapSettings = {},
    setMapSettings = () => { },
    state = {},
    timeZone,
  } = props;
  const {
    assetData = {},
    assetHistories = [],
    histories: historiesFromProps = [],
    trips = [],
  } = state;

  const { propertiesMap = {} } = assetData;

  // I hate that we have this here. It should just be coming from one source.
  // But this got goofed at some point. So im making a catch for it for now.
  const historiesSource =
    assetHistories?.length > 0 ? assetHistories : historiesFromProps;

  const histories = useMemo(() => {
    if (Array.isArray(historiesSource)) return historiesSource;
    const fromSource = historiesSource?.histories;
    return Array.isArray(fromSource) ? fromSource : [];
  }, [historiesSource]);

  const renderMarkers = (Array.isArray(histories) ? histories : [])
    .map((event) => {
      // If the current event does not have a lat/lon, we check to see if a facilityId exist.
      // If so, we use the facilityId.location.lat and lon. Otherwise we use the event.lat and lon
      if (
        event &&
        (event.latitude !== 0 || event.longitude !== 0) &&
        event.latitude &&
        event.longitude
      ) {
        return event;
      } else if (
        event &&
        (event.latitude !== 0 || event.longitude !== 0) &&
        event.facilityId
      ) {
        const currentFacility = facilities[event.facilityId] || {};
        const { location = {} } = currentFacility;
        const { latitude, longitude } = location;
        return { ...event, latitude, longitude };
      } else {
        return null;
      }
    })
    .filter((event) => event !== null)
    .sort((a, b) => (a?.timeOfLog ?? 0) - (b?.timeOfLog ?? 0));

  const popUpContent = (marker, opts = {}) => {
    const {
      event = null,
      facilityId = null,
      latitude = 0,
      longitude = 0,
      timeOfLog = null,
    } = marker;

    const facilityName = facilityId && facilities[facilityId] && latitude !== 0 && longitude !== 0 
      ? facilities[facilityId].name 
      : null;

    return (
      <MapPopupContent
        title={`Last Event: ${event || "Unknown"}`}
        subtitle={propertiesMap?.description || null}
        lastEvent={event}
        description={propertiesMap?.description || null}
        note={propertiesMap?.note || null}
        facility={facilityName}
        date={timeOfLog ? utc_to_local_12_hour_clock(timeOfLog, timeZone) : null}
        showButton={false}
        onClose={opts?.onClose}
      />
    );
  };

  const polylinePopUpContent = (polyline) => {
    const { data = {} } = polyline;
    const { label = "", path = [] } = data;
    const tripStart = path[0].timeOfLogLong
      ? moment(path[0].timeOfLogLong).format("MM/DD/YYYY hh:mm A")
      : "";
    const tripEnd = path[path.length - 1].timeOfLogLong
      ? moment(path[path.length - 1].timeOfLogLong).format("MM/DD/YYYY hh:mm A")
      : "";
    const duration =
      path.length > 0
        ? moment
          .duration(
            path[path.length - 1].timeOfLogLong - path[0].timeOfLogLong
          )
          .humanize()
        : "";

    return (
      (<Grid container>
        {/* Label */}
        <Grid size={12}>
          <Typography variant="h6">{label}</Typography>
        </Grid>
        {/* From */}
        <Grid sx={{ textAlign: "left" }} size={12}>
          <Typography component="p" variant="h6">
            Trip Start:{" "}
          </Typography>
          <Typography variant="subtitle1"> {tripStart} </Typography>
        </Grid>
        {/* To */}
        <Grid sx={{ textAlign: "left" }} size={12}>
          <Typography component="p" variant="h6">
            Trip End:
          </Typography>
          <Typography variant="subtitle1"> {tripEnd} </Typography>
        </Grid>
        {/* Trip Duration */}
        <Grid sx={{ textAlign: "left" }} size={12}>
          <Typography component="p" variant="h6">
            Trip Duration:
          </Typography>
          <Typography variant="subtitle1"> {duration} </Typography>
        </Grid>
      </Grid>)
    );
  };

  useEffect(() => {
    setMapSettings({ center: { lat: 34.561187, lng: -40.814914 }, zoom: 2.3 });
  }, [setMapSettings]);

  const hasSetInitialViewRef = useRef(false);

  useEffect(() => {
    if (hasSetInitialViewRef.current) return;
    const defaultLat = 60.363007;
    const defaultLon = -43.6339;
    let firstItemInHistoriesWithAFacility = histories.find(
      (element) => element.facilityId && !element.longitude && !element.latitude
    );
    let firstItemInHistoriesWithALatLon = histories.find(
      (element) => element.latitude && element.longitude
    );

    // If the first item in the array does not have a lat/lon but does have a facility id
    // Otherwise check to see if the first item has valid lat/lon values. If there are no lat/lon values to use
    // we default the user to Austin, Tx.
    if (histories && firstItemInHistoriesWithAFacility) {
      const currentFacility =
        facilities[firstItemInHistoriesWithAFacility?.facilityId] || {};
      const { location = {} } = currentFacility;
      const {
        latitude: currentLat = defaultLat,
        longitude: currentLon = defaultLon,
      } = location;

      // Verify the facility has a valid Lat/Lon
      if (currentLat && currentLon) {
        setMapSettings({
          center: { lat: currentLat, lng: currentLon },
          zoom: 5,
        });
        hasSetInitialViewRef.current = true;
      }
    } else if (histories && firstItemInHistoriesWithALatLon) {
      setMapSettings({
        center: {
          lat: firstItemInHistoriesWithALatLon.latitude,
          lng: firstItemInHistoriesWithALatLon.longitude,
        },
        zoom: 5,
      });
      hasSetInitialViewRef.current = true;
    } else if (!histories || histories.length === 0) {
      // Default view if no pins are available
      setMapSettings({
        center: { lat: defaultLat, lng: defaultLon },
        zoom: 2.3,
      });
    }
  }, [facilities, histories, setMapSettings]);

  return (
    <GoogleMapComponent
      ref={ref}
      activeMarker={activeMarker}
      emphasizeLatestMarker={true}
      emptyStateMessage={renderMarkers.length === 0 ? "No location history to display" : null}
      eventTypesMap={eventTypesMap}
      mapSettings={mapSettings}
      mapTypeId={mapSettings?.mapTypeId || "satellite"}
      markerKey="assetHistoryId"
      markersToRender={renderMarkers}
      polylinePopUpContent={polylinePopUpContent}
      polylines={trips}
      popUpContent={popUpContent}
      setActiveMarker={setActiveMarker}
      setMapSettings={setMapSettings}
      showJourneyLine={true}
      showTimestampsOnMarkers={true}
      timeZone={timeZone}
    />
  );
});

export default Map;
